import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc as firestoreDoc,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../../firebase';

const ITEMS_PER_PAGE = 15;

export default function BannerList({ onCount }) {
  const [banners, setBanners] = useState([]);
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const navigate = useNavigate();

  // Load banners
  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, 'banners'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setBanners(list);
      onCount?.(list.length);
      setPage(1);
    })();
  }, [onCount]);

  // Toggle exposure
  const handleExposureToggle = async (id, checked) => {
    // 1) banners 컬렉션 내 exposed 필드 업데이트
    await updateDoc(firestoreDoc(db, 'banners', id), { exposed: checked });
    setBanners(bs => bs.map(b => (b.id === id ? { ...b, exposed: checked } : b)));

    // 2) data/bannerList 문서 안 'banner' 배열에 id 추가/제거
    const bannerListRef = firestoreDoc(db, 'data', 'bannerList');
    if (checked) {
      await updateDoc(bannerListRef, { banner: arrayUnion(id) });
    } else {
      await updateDoc(bannerListRef, { banner: arrayRemove(id) });
    }
  };

  // Delete banner
  const handleDelete = async id => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    await deleteDoc(firestoreDoc(db, 'banners', id));
    setBanners(bs => bs.filter(b => b.id !== id));

    // 삭제 시 data/bannerList의 'banner' 배열에서도 제거
    const bannerListRef = firestoreDoc(db, 'data', 'bannerList');
    await updateDoc(bannerListRef, { banner: arrayRemove(id) });
  };

  // Sorting
  const handleSort = key => {
    setSortConfig(sc => ({
      key,
      direction: sc.key === key && sc.direction === 'asc' ? 'desc' : 'asc'
    }));
    setPage(1);
  };

  const sortedBanners = useMemo(() => {
    const arr = [...banners];
    const { key, direction } = sortConfig;
    if (!key) return arr;
    arr.sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];
      if (key === 'exposed') {
        aVal = aVal ? 1 : 0;
        bVal = bVal ? 1 : 0;
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [banners, sortConfig]);

  const pageCount = Math.ceil(sortedBanners.length / ITEMS_PER_PAGE);
  const startIdx = (page - 1) * ITEMS_PER_PAGE;
  const current = sortedBanners.slice(startIdx, startIdx + ITEMS_PER_PAGE);

  const renderHeader = (label, key) => (
    <th
      className="p-3 text-center cursor-pointer select-none"
      onClick={() => handleSort(key)}
    >
      <div className="inline-flex items-center justify-center">
        <span>{label}</span>
        <span className="inline-block w-4 text-center">
          {sortConfig.key === key
            ? (sortConfig.direction === 'asc' ? '▲' : '▼')
            : '\u00A0'}
        </span>
      </div>
    </th>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mt-4 mb-4">
        <h1 className="text-2xl font-bold">배너 관리 대시보드</h1>
        <button
          onClick={() => navigate('/admin/banner/create')}
          className="px-4 py-2 border border-green-600 text-green-600 rounded hover:bg-green-50"
        >
          배너 등록
        </button>
      </div>

      {/* Table */}
      <div className="overflow-auto bg-neutral-800 shadow rounded">
        <table className="min-w-full table-fixed">
          <thead>
            <tr className="bg-neutral-700">
              {renderHeader('노출', 'exposed')}
              {renderHeader('배너 이름', 'title')}
              {renderHeader('태그', 'tag')}
              {renderHeader('링크 ID', 'linkId')}
              <th className="p-3 text-center w-1/5">수정</th>
              <th className="p-3 text-right w-1/12"></th>
            </tr>
          </thead>
          <tbody>
            {current.map(b => (
              <tr key={b.id} className="border-t">
                <td className="p-3 text-center">
                  <input
                    type="checkbox"
                    checked={b.exposed}
                    onChange={e => handleExposureToggle(b.id, e.target.checked)}
                  />
                </td>
                <td className="p-3 text-center">{b.title}</td>
                <td className="p-3 text-center">{b.tag}</td>
                <td className="p-3 text-center">{b.linkId}</td>
                <td className="p-3 text-center space-x-2">
                  <button
                    onClick={() => navigate(`/admin/banner/edit/${b.id}`)}
                    className="px-3 py-1 border border-blue-600 text-blue-600 rounded hover:bg-blue-50 text-sm"
                  >
                    수정
                  </button>
                </td>
                <td className="p-3 text-right">
                  <button
                    onClick={() => handleDelete(b.id)}
                    className="px-3 py-1 border border-red-600 text-red-600 rounded hover:bg-red-50 text-sm"
                  >
                    삭제
                  </button>
                </td>
              </tr>
            ))}
            {banners.length === 0 && (
              <tr>
                <td colSpan="6" className="p-4 text-center text-gray-400">
                  등록된 배너가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pageCount > 1 && (
        <div className="flex justify-center space-x-2 p-4">
          {Array.from({ length: pageCount }, (_, i) => i + 1).map(n => (
            <button
              key={n}
              onClick={() => setPage(n)}
              className={`px-3 py-1 rounded ${n === page
                ? 'bg-green-600 text-white'
                : 'bg-neutral-700 text-gray-200 hover:bg-neutral-600'}`}
            >
              {n}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
